import React, { useCallback, useMemo } from 'react';
import { Link as BaseLink, Box, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ColumnDef,
} from 'material-react-table';
import { IntlShape, useIntl } from 'react-intl';

import {
  CREATE_OBJECT_GROUP_MUTATION,
  GET_MY_OBJECT_GROUPS_QUERY,
} from '../../gql';
import { formatDate } from '../../helpers';
import { useMaterialReactTableLocalization } from '../../i18n';

interface ObjectGroupRow {
  id: string;
  name?: string | null;
  description?: string | null;
  createdAt?: string | null;
  shared?: boolean | null;
  level?: number | null;
}

const createColumns = (intl: IntlShape): MRT_ColumnDef<ObjectGroupRow>[] => [
  {
    accessorKey: 'name',
    header: intl.formatMessage({
      id: 'objects.groups.table.name',
      defaultMessage: 'Name',
    }),
    size: 220,
    Cell: ({ cell, renderedCellValue }) => (
      <BaseLink
        underline="none"
        component={Link}
        to={`${cell.row.original.id}`}
      >
        <span>
          {renderedCellValue || (
            <i>
              {intl.formatMessage({
                id: 'objects.groups.unnamed',
                defaultMessage: 'Unnamed group',
              })}
            </i>
          )}
        </span>
      </BaseLink>
    ),
  },
  {
    accessorKey: 'description',
    header: intl.formatMessage({
      id: 'objects.groups.table.description',
      defaultMessage: 'Description',
    }),
    size: 260,
    Cell: ({ cell }) => cell.getValue<string | null>() ?? '-',
  },
  {
    accessorKey: 'shared',
    header: intl.formatMessage({
      id: 'objects.groups.table.ownership',
      defaultMessage: 'Ownership',
    }),
    size: 130,
    Cell: ({ cell }) =>
      cell.getValue()
        ? intl.formatMessage({
            id: 'objects.groups.sharedWithMe',
            defaultMessage: 'Shared with me',
          })
        : intl.formatMessage({
            id: 'objects.groups.myGroup',
            defaultMessage: 'My group',
          }),
  },
  {
    accessorFn: (row) => row.createdAt && new Date(row.createdAt),
    header: intl.formatMessage({
      id: 'objects.groups.table.createdAt',
      defaultMessage: 'Date created',
    }),
    size: 130,
    Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
  },
];

const gridStyles = { marginBottom: 6, display: 'grid' };
const toolbarStyles = { display: 'flex', gap: 1, p: 0.5 };

interface ObjectGroupsTableProps {
  rows: ObjectGroupRow[];
  loading?: boolean;
}

const ObjectGroupsTable = ({
  rows,
  loading = false,
}: ObjectGroupsTableProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const localization = useMaterialReactTableLocalization();
  const [
    createGroup,
    { loading: creating },
  ] = useMutation(CREATE_OBJECT_GROUP_MUTATION, {
    refetchQueries: [{ query: GET_MY_OBJECT_GROUPS_QUERY }],
  });

  const columns = useMemo(() => createColumns(intl), [intl]);
  const handleCreate = useCallback(async () => {
    const res = await createGroup({
      variables: {
        group: {
          name: intl.formatMessage({
            id: 'objects.groups.newName',
            defaultMessage: 'New group',
          }),
        },
      },
    });
    const payload = res.data?.createObjectGroup;
    if (payload?.__typename === 'CreateObjectGroupSuccess') {
      navigate(payload.group.id);
    }
  }, [createGroup, intl, navigate]);
  const renderToolbarActions = useCallback(
    () => (
      <Box sx={toolbarStyles}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCreate}
          disabled={creating}
        >
          {intl.formatMessage({
            id: 'objects.groups.create',
            defaultMessage: 'Create group',
          })}
        </Button>
      </Box>
    ),
    [creating, handleCreate, intl]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'compact',
    },
    state: { isLoading: loading },
    muiPaginationProps: { showRowsPerPage: false },
    muiTablePaperProps: { sx: gridStyles },
    renderTopToolbarCustomActions: renderToolbarActions,
  });

  return <MaterialReactTable table={table} />;
};

export { ObjectGroupsTable };
